// Risk Calculation Service
// RPN Calculation Engine for Phase 2
// Implements comprehensive RPN calculation with contextual factors, trends, and comparisons

import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { riskService } from './riskService';

export class RiskCalculationService {
  constructor() {
    this.calculationsCollection = collection(db, 'risk_calculations');
  }
  
  /**
   * Basic RPN calculation
   * RPN = Severity × Occurrence × Detection
   */
  calculateRPN(severity, occurrence, detection) {
    if (!this.validateRPNComponents(severity, occurrence, detection)) {
      throw new Error('Invalid RPN components. All values must be integers between 1 and 10.');
    }
    return severity * occurrence * detection;
  }
  
  /**
   * Get risk level from RPN with full details
   */
  getRiskLevel(rpn) {
    if (rpn >= 500) {
      return { 
        level: 'Critical', 
        color: '#dc3545', 
        action: 'immediate',
        description: 'Critical risk - Immediate action required'
      };
    }
    if (rpn >= 200) {
      return { 
        level: 'High', 
        color: '#fd7e14', 
        action: 'urgent',
        description: 'High risk - Urgent action required'
      };
    }
    if (rpn >= 50) {
      return { 
        level: 'Medium', 
        color: '#ffc107', 
        action: 'scheduled',
        description: 'Medium risk - Scheduled review required'
      };
    }
    return { 
      level: 'Low', 
      color: '#28a745', 
      action: 'routine',
      description: 'Low risk - Routine monitoring'
    };
  }
  
  /**
   * Apply contextual factors to base RPN
   * Multiplies base RPN by each factor's multiplier
   */
  applyContextualFactors(baseRPN, factors) {
    if (!Array.isArray(factors) || factors.length === 0) {
      return baseRPN;
    }
    
    let adjustedRPN = baseRPN;
    factors.forEach(factor => {
      const multiplier = factor.risk_multiplier || factor.rpnMultiplier || 1.0;
      if (multiplier > 0 && multiplier <= 3.0) {
        adjustedRPN *= multiplier;
      }
    });
    
    return Math.round(Math.min(1000, Math.max(1, adjustedRPN)));
  }
  
  /**
   * Validate RPN components
   * All values must be integers between 1 and 10
   */
  validateRPNComponents(severity, occurrence, detection) {
    const values = [severity, occurrence, detection];
    return values.every(
      val => val >= 1 && val <= 10 && Number.isInteger(val)
    );
  }
  
  /**
   * Get RPN trend for a protocol over time
   * Returns daily RPN values for the specified number of days
   */
  async getRPNTrend(protocolId, days = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      
      const q = query(
        this.calculationsCollection,
        where('protocolId', '==', protocolId),
        where('timestamp', '>=', startTimestamp),
        where('timestamp', '<=', endTimestamp),
        orderBy('timestamp', 'asc')
      );
      
      const snapshot = await getDocs(q);
      
      // Group by date
      const dailyRPN = new Map();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const date = data.timestamp.toDate();
        const dateKey = date.toISOString().split('T')[0];
        
        if (!dailyRPN.has(dateKey)) {
          dailyRPN.set(dateKey, {
            date: dateKey,
            rpn: [],
            count: 0
          });
        }
        
        const dayData = dailyRPN.get(dateKey);
        dayData.rpn.push(data.currentRPN || data.rpn || 0);
        dayData.count += 1;
      });
      
      // Calculate average RPN per day
      const trend = Array.from(dailyRPN.values()).map(dayData => ({
        date: dayData.date,
        averageRPN: dayData.rpn.reduce((sum, rpn) => sum + rpn, 0) / dayData.rpn.length,
        minRPN: Math.min(...dayData.rpn),
        maxRPN: Math.max(...dayData.rpn),
        calculationCount: dayData.count
      }));
      
      return trend;
    } catch (error) {
      console.error('Error fetching RPN trend:', error);
      return [];
    }
  }
  
  /**
   * Compare RPN across multiple protocols
   * Returns comparison matrix with statistics
   */
  async compareProtocolRisks(protocolIds) {
    try {
      const comparisonData = await Promise.all(
        protocolIds.map(async (protocolId) => {
          const risks = await riskService.getProtocolRisks(protocolId);
          
          if (risks.length === 0) {
            return {
              protocolId,
              riskCount: 0,
              totalRPN: 0,
              averageRPN: 0,
              maxRPN: 0,
              criticalityDistribution: {
                Critical: 0,
                High: 0,
                Medium: 0,
                Low: 0
              }
            };
          }
          
          const rpnValues = risks.map(r => r.rpn || 0);
          const totalRPN = rpnValues.reduce((sum, rpn) => sum + rpn, 0);
          const averageRPN = totalRPN / risks.length;
          const maxRPN = Math.max(...rpnValues);
          
          const criticalityDistribution = {
            Critical: risks.filter(r => r.criticality === 'Critical').length,
            High: risks.filter(r => r.criticality === 'High').length,
            Medium: risks.filter(r => r.criticality === 'Medium').length,
            Low: risks.filter(r => r.criticality === 'Low').length
          };
          
          return {
            protocolId,
            protocolName: risks[0]?.protocolName || 'Unknown',
            riskCount: risks.length,
            totalRPN,
            averageRPN: Math.round(averageRPN * 100) / 100,
            maxRPN,
            criticalityDistribution
          };
        })
      );
      
      // Sort by average RPN descending
      comparisonData.sort((a, b) => b.averageRPN - a.averageRPN);
      
      return {
        protocols: comparisonData,
        summary: {
          totalProtocols: protocolIds.length,
          totalRisks: comparisonData.reduce((sum, p) => sum + p.riskCount, 0),
          highestAverageRPN: comparisonData[0]?.averageRPN || 0,
          lowestAverageRPN: comparisonData[comparisonData.length - 1]?.averageRPN || 0
        }
      };
    } catch (error) {
      console.error('Error comparing protocol risks:', error);
      throw error;
    }
  }
  
  /**
   * Calculate protocol aggregate RPN
   * Sums all RPN values from associated risks
   */
  async calculateProtocolAggregateRPN(protocolId) {
    try {
      const risks = await riskService.getProtocolRisks(protocolId);
      
      if (risks.length === 0) {
        return {
          totalRPN: 0,
          averageRPN: 0,
          maxRPN: 0,
          riskCount: 0,
          criticalityLevel: 'Low'
        };
      }
      
      const rpnValues = risks.map(r => r.rpn || 0);
      const totalRPN = rpnValues.reduce((sum, rpn) => sum + rpn, 0);
      const averageRPN = totalRPN / risks.length;
      const maxRPN = Math.max(...rpnValues);
      
      // Determine overall criticality based on max RPN
      const riskLevel = this.getRiskLevel(maxRPN);
      
      return {
        totalRPN,
        averageRPN: Math.round(averageRPN * 100) / 100,
        maxRPN,
        riskCount: risks.length,
        criticalityLevel: riskLevel.level,
        riskLevelDetails: riskLevel
      };
    } catch (error) {
      console.error('Error calculating protocol aggregate RPN:', error);
      throw error;
    }
  }
  
  /**
   * Save RPN calculation to history
   * Used for tracking RPN changes over time
   */
  async saveCalculation(calculationData) {
    try {
      const now = Timestamp.now();
      
      const calculation = {
        ...calculationData,
        timestamp: now,
        createdAt: now
      };
      
      const docRef = await addDoc(this.calculationsCollection, calculation);
      return docRef.id;
    } catch (error) {
      console.error('Error saving RPN calculation:', error);
      throw error;
    }
  }
  
  /**
   * Get historical calculations for a protocol
   */
  async getCalculationHistory(protocolId, limit = 100) {
    try {
      const q = query(
        this.calculationsCollection,
        where('protocolId', '==', protocolId),
        orderBy('timestamp', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.slice(0, limit).map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching calculation history:', error);
      return [];
    }
  }
}

export const riskCalculationService = new RiskCalculationService();



